import { Controller } from '@hotwired/stimulus';

/**
 * Turns an image file field into a click-or-drag drop zone.
 *
 * The native input stays in the DOM (visually hidden, still focusable) so the
 * form submits exactly as before; dropped files are copied into it with a
 * DataTransfer so no extra upload plumbing is needed.
 */
export default class extends Controller {
  static targets = ['input', 'zone', 'image', 'placeholder', 'filename', 'error', 'remove'];
  static values = { maxBytes: { type: Number, default: 0 } };

  connect() {
    this.dragDepth = 0;
    this.previewUrl = null;
    this.defaultHint = this.hasFilenameTarget ? this.filenameTarget.textContent : '';
  }

  disconnect() {
    this.releasePreview();
  }

  dragEnter(event) {
    if (!this.draggingFiles(event)) return;

    event.preventDefault();
    this.dragDepth += 1;
    this.zoneTarget.classList.add('is-dragover');
  }

  dragOver(event) {
    if (!this.draggingFiles(event)) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  dragLeave() {
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) this.zoneTarget.classList.remove('is-dragover');
  }

  drop(event) {
    if (!this.draggingFiles(event)) return;

    event.preventDefault();
    this.dragDepth = 0;
    this.zoneTarget.classList.remove('is-dragover');

    const file = event.dataTransfer.files[0];
    if (!file || !this.acceptable(file)) return;

    const transfer = new DataTransfer();
    transfer.items.add(file);
    this.inputTarget.files = transfer.files;
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  inputChanged() {
    const file = this.inputTarget.files?.[0];
    if (!file) return;

    if (!this.acceptable(file)) {
      this.inputTarget.value = '';
      return;
    }

    this.showPreview(file);
  }

  showPreview(file) {
    this.clearError();
    this.releasePreview();
    this.previewUrl = URL.createObjectURL(file);

    if (this.hasImageTarget) {
      this.imageTarget.src = this.previewUrl;
      this.imageTarget.hidden = false;
    }
    if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = true;
    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = `${file.name} · ${this.humanSize(file.size)}`;
    }
    // Picking a new file supersedes a pending removal.
    if (this.hasRemoveTarget) this.removeTarget.checked = false;
  }

  acceptable(file) {
    const allowed = (this.inputTarget.accept || '')
      .split(',')
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean);

    const type = (file.type || '').toLowerCase();
    const typeOk = allowed.length === 0
      || allowed.some((pattern) => (pattern.endsWith('/*')
        ? type.startsWith(pattern.slice(0, -1))
        : pattern === type));

    if (!typeOk) {
      this.setError('That file type is not supported. Use a JPEG, PNG, WebP, or GIF.');
      return false;
    }

    if (this.maxBytesValue > 0 && file.size > this.maxBytesValue) {
      this.setError(`That image is ${this.humanSize(file.size)}. Keep it under ${this.humanSize(this.maxBytesValue)}.`);
      return false;
    }

    return true;
  }

  draggingFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  setError(message) {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (!this.hasErrorTarget) return;

    this.errorTarget.textContent = '';
    this.errorTarget.hidden = true;
  }

  releasePreview() {
    if (!this.previewUrl) return;

    URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }

  humanSize(bytes) {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} bytes`;
  }
}
